import { useEffect, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuthState } from '../authState';
import { captureReturnTo } from '../returnTo';
import { PageLoader } from '@/shared/components/PageLoader';

/**
 * Gate for every non-public route: an anonymous visitor never renders `children`,
 * they are sent to the IdP instead.
 *
 * The three states are kept distinct on purpose. While the OIDC session check is
 * still in flight we know nothing yet, so we show the loader rather than a blank
 * page — and crucially we do NOT redirect, because a stored session may be about
 * to resolve and bouncing to the IdP would throw it away.
 *
 * The page being refused travels with the redirect, so login returns the visitor to it instead of
 * to the upload form. That matters most for the one link users do not type themselves: the
 * "your document is ready" email. COPA-505.
 */
export function RequireAuth({ children }: Readonly<{ children: ReactNode }>) {
  const { isAuthenticated, isLoading, signinRedirect } = useAuthState();
  const returnTo = captureReturnTo(useLocation());

  useEffect(() => {
    if (!isLoading && !isAuthenticated) signinRedirect(returnTo);
  }, [isLoading, isAuthenticated, signinRedirect, returnTo]);

  if (isLoading) return <PageLoader />;
  // Definitively anonymous: render nothing while the effect above navigates away.
  if (!isAuthenticated) return null;
  return <>{children}</>;
}
